import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { SettingsItem } from "@msuite/ui/settings";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import React from "react";
import { Can } from "@/components/citadel";
import { client } from "@/hono";
import { useIncomingInvoice } from "@/hooks/use-incoming-invoice";
import type { IncomingInvoicePanelBaseProps } from "../../shared";

/**
 * Reset the invoice's review: every approval removed, the default approvers asked again, the
 * Kürzung lifted. An approved invoice can be changed only after it; a paid one has its payment
 * taken back first, and a duplicate is merged or declared none instead — the route refuses both.
 */
export function ResetIncomingInvoiceReview({ incomingInvoiceId }: IncomingInvoicePanelBaseProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const confirmModal = useAsyncModal();
	const resetLoader = useLoading();
	const qc = useQueryClient();

	/** Query */
	const { incomingInvoice } = useIncomingInvoice(incomingInvoiceId);

	/** Functions */
	async function handleOnReset() {
		if (!(await confirmModal.confirm())) return;
		try {
			resetLoader.start();
			const response = await client.incoming_invoices[":incomingInvoiceId"].approvals.reset.$post({
				param: { incomingInvoiceId },
			});
			await responseHandler(response);
			await qc.invalidateQueries({
				predicate: invalidate(["incoming-invoices", "incoming-invoice-approvals-mine"]),
			});
			toast.success(t("incoming_invoices.reset.done"));
		} finally {
			resetLoader.stop();
		}
	}

	/** Computed */
	const state = incomingInvoice?.state;
	const isRefused = state === "paid" || state === "duplicate" || state === "merged";

	/** Render */
	return (
		<React.Fragment>
			<SettingsItem
				title={t("incoming_invoices.reset.title")}
				description={t(
					state === "paid"
						? "incoming_invoices.reset.paid_refused"
						: "incoming_invoices.reset.description",
				)}
			>
				<Can
					action="delete"
					subject="IncomingInvoice"
					resource={incomingInvoice ? { ...incomingInvoice } : undefined}
				>
					<Button
						isLoading={confirmModal.isPending || resetLoader.isLoading}
						isDisabled={!incomingInvoice || isRefused}
						variant="danger-ghost"
						onClick={handleOnReset}
						data-cy="reset-incoming-invoice-review-button"
					>
						{t("incoming_invoices.reset.button")}
					</Button>
				</Can>
			</SettingsItem>
			{confirmModal.isOpen && (
				<ConfirmModal
					title={t("incoming_invoices.reset.title")}
					message={t("incoming_invoices.reset.confirm_message")}
					controller={confirmModal}
				/>
			)}
		</React.Fragment>
	);
}
